import RootPage from "containers/RootPage";
import { useSenderAccountContext } from "context/SenderAccountContext";
import {
  ACCOUNT_ROLE_CARESEEKER_USER_MANAGEMENT,
  ACCOUNT_ROLE_DTA_APPROVER,
  ACCOUNT_ROLE_PREDICT,
  ACCOUNT_ROLE_REPORT,
  ACCOUNT_ROLE_SENDER_IT,
  CREATE_PATIENT_ROLES,
  PATIENT_TAB_FILES,
  PATIENT_TAB_MAP,
  PATIENT_TAB_PROFILE,
  PATIENT_TAB_REHAB_FORMS,
  PATIENT_TAB_SEARCH,
  PATIENT_TAB_TIMELINE,
  PATIENT_TAB_TRANSITIONAL_CARE,
  REDIRECT_PARAM,
  REHAB_FORM_APPROVAL_PAGE,
  TRACK_EVENTS,
} from "core/consts";
import { PATHS } from "core/consts/pages";
import {
  TOKEN_STATUS,
  checkTokenStatus,
  hasAccessToAiChat,
  hasAccessToDTAApproval,
  hasAccessToOnPremDebug,
  hasAccessToPatientDashboard,
  hasAccessToPredict,
  hasAccessToReport,
  hasAccessToUserManagement,
  isStaffAccount,
  useHasAccessToAiChat,
} from "core/model/accounts";
import { ExtractDocsProvider } from "core/model/api/endpoints/extractDocs/context";
import { isFilesActivatedAtAuctionLevel } from "core/model/auctions";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { activatePredict } from "core/model/utils/featureFlags";
import { Account, AccountRole, ProductFeature } from "core/types";
import SpinnerPage, {
  SpinnerPageContainer,
} from "ds_legacy/components/Spinner";
import { UnsupportedBrowser } from "dsl/atoms/ErrorPages";
import RedirectRoot from "dsl/atoms/RedirectRoot";
import { SearchCandidatesProvider } from "dsl/atoms/SearchCandidates";
import ThrowError from "dsl/atoms/ThrowError";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import lazy from "dsl/atoms/retry";
import { CareseekerAuthPage } from "dsl/ecosystems/CareseekerAuthPage";
import { areNewRehabFormsActive } from "dsl/ecosystems/PatientForms/RehabForms/utils";
import { getTransitionalCareAction } from "dsl/ecosystems/PatientForms/TransitionalCare";
import PatientHome, {
  PatientHomeMenu,
  usePatientHomeContext,
} from "dsl/ecosystems/PatientHome/connect";
import { RecareDocsProvider } from "dsl/ecosystems/RecareDocs/context";
import { AssessmentPatientMenu as AssessmentMenu } from "dsl/organisms/PatientMenu";
import { DebugPage, RequireCondition } from "entries/receiver/Router/helpers";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useTracking } from "react-tracking";
import {
  useAppDispatch,
  useAppSelector,
  useConnecterWithCareseeker,
  useIsLoggedOnClinicApp,
  useLoggedCareseeker,
} from "reduxentities/hooks";
import { selectCareseekers, selectCredentials } from "reduxentities/selectors";
import { CARESEEKER_CHANGED, LOGGED_OUT } from "reduxentities/slices/authSlice";
import { useTranslations } from "translations";
import { getRedirectPathBasedOnRoles } from "./utils";

export const defaultRedirectPath = "/app/clinic/dashboard/todo/active";
export const dtaApproverPath = "/app/clinic/dashboard/rehab-forms-approval";
export const onpremDebugRedirectPath = "/app/clinic/dashboard/onpremsetup/test";
export const predictRedirectPath = "/app/clinic/dashboard/predict";
export const aiChatRedirectPath = PATHS.APP.CHAT;
export const reportPath = "/app/clinic/dashboard/report";
export const userManagementRedirectPath = "/app/clinic/dashboard/users";

const RehabFormApprovalPage = lazy(
  () => import("dsl/ecosystems/RehabFormApprovalPage"),
);
const ForgotPasswordPage = lazy(
  () => import("dsl/ecosystems/ForgotPasswordPage"),
);
const OnPremiseDebugPage = lazy(
  () => import("dsl/ecosystems/OnPremiseDebugPage"),
);

const SenderDashboardPage = lazy(
  () => import("dsl/ecosystems/SenderDashboard"),
);
const ReportPage = lazy(
  () => import("dsl/organisms/Careseeker/ClinicReportPage"),
);
const UserManagementPage = lazy(
  () => import("dsl/ecosystems/UserManagementPage"),
);
const PredictPage = lazy(() => import("dsl/ecosystems/PredictPage"));
const PatientDetailsPage = lazy(
  () => import("dsl/ecosystems/PredictDetailsPage"),
);
const PredictStationGuard = lazy(
  () => import("dsl/ecosystems/PredictDetailsPage/PredictStationGuard"),
);
const BarthelSummaryPage = lazy(
  () =>
    import("dsl/ecosystems/IndependenceScaleSummaryPage/BarthelSummaryPage"),
);
const EarlyRehabSummaryPage = lazy(
  () =>
    import("dsl/ecosystems/IndependenceScaleSummaryPage/EarlyRehabSummaryPage"),
);
const SingerSummaryPage = lazy(
  () => import("dsl/ecosystems/IndependenceScaleSummaryPage/SingerSummaryPage"),
);
const DiagnosticPage = lazy(() => import("dsl/organisms/DiagnosticPage"));
const LogoutPage = lazy(() => import("dsl/ecosystems/LogoutPage"));
const PrintableSenderDashboardPage = lazy(
  () => import("dsl/ecosystems/SenderDashboard/PrintableSenderDashboard"),
);
const PatientCreatePage = lazy(
  () => import("dsl/ecosystems/PatientCreatePage"),
);
const PasswordResetPageV2 = lazy(
  () => import("dsl/organisms/PasswordResetPageV2"),
);
const PasswordChangePageV2 = lazy(
  () => import("dsl/ecosystems/PasswordChangePageV2"),
);
const ClinicDashboard = lazy(() => import("dsl/ecosystems/ClinicDashboard"));
const DischargeConfirmation = lazy(
  () => import("dsl/ecosystems/DischargeConfirmation"),
);
const PatientFiles = lazy(() => import("dsl/ecosystems/PatientFiles"));
const PatientProfile = lazy(() => import("dsl/ecosystems/PatientProfile"));
const PatientSearch = lazy(() => import("dsl/ecosystems/PatientSearchMerge"));
const PatientAssessment = lazy(
  () => import("dsl/ecosystems/PatientAssessment"),
);
const PatientTimeline = lazy(() => import("dsl/ecosystems/AuctionTimeline"));
const AuctionResponse = lazy(
  () => import("dsl/ecosystems/AuctionResponse/connect"),
);
const PatientMap = lazy(() => import("dsl/ecosystems/PatientMap"));
const PatientTransitionalCare = lazy(
  () => import("dsl/ecosystems/PatientForms/TransitionalCare"),
);
const PrintableSearchMergePanel = lazy(
  () => import("dsl/ecosystems/PatientSearchMerge/print"),
);
const PatientRehabFormsHome = lazy(
  () => import("dsl/ecosystems/PatientForms/RehabForms"),
);
const RehabFormRenderer = lazy(
  () => import("dsl/ecosystems/PatientForms/RehabForms/FormRenderer"),
);
const AiChat = lazy(() => import("dsl/ecosystems/AiChat"));

const getPriorityRoute = (
  account: Account | null | undefined,
  activeRoles: number[] | undefined,
  product_features: ProductFeature[] | undefined,
): string => {
  if (!activeRoles || activeRoles.length === 0) {
    return "/logout?reason=unauthorized";
  }

  if (hasAccessToPatientDashboard(account, activeRoles)) {
    return defaultRedirectPath;
  }
  if (hasAccessToUserManagement(account, activeRoles)) {
    return userManagementRedirectPath;
  }
  if (hasAccessToPredict(account, activeRoles, product_features)) {
    return predictRedirectPath;
  }
  if (hasAccessToReport(account, activeRoles)) {
    return reportPath;
  }
  if (hasAccessToOnPremDebug(account, activeRoles)) {
    return onpremDebugRedirectPath;
  }
  if (hasAccessToDTAApproval(account, activeRoles)) {
    return dtaApproverPath;
  }
  if (hasAccessToAiChat(product_features)) {
    return aiChatRedirectPath;
  }

  return "/logout?reason=unauthorized";
};

const EmptyPage = () => <div style={{ flex: "1" }} />;

function RequireAiChatAccess({ redirectTo }: { redirectTo: string }) {
  const hasAccess = useHasAccessToAiChat();
  return <RequireCondition hasCondition={hasAccess} redirectTo={redirectTo} />;
}

function CheckRoles({
  children,
  defaultRoute,
  setDefaultRoute,
}: {
  children?: ReactNode;
  defaultRoute: string;
  setDefaultRoute: Dispatch<SetStateAction<string>>;
}) {
  const navigate = useNavigate();
  const { account, activeRoles } = useSenderAccountContext();

  useEffect(() => {
    const desiredPath = getRedirectPathBasedOnRoles({ account, activeRoles });

    if (defaultRoute !== desiredPath) {
      setDefaultRoute(desiredPath);
      navigate(desiredPath);
    }
  }, [defaultRoute, account, activeRoles]);

  return <>{children ?? <Outlet />}</>;
}

function CareseekerAuthCheck({ children }: { children?: ReactNode | null }) {
  const { trackEvent } = useTracking();
  const dispatch = useAppDispatch();
  const logged = useIsLoggedOnClinicApp();
  const credentials = useAppSelector(selectCredentials);

  useEffect(() => {
    const tokenStatus = checkTokenStatus(credentials);
    if (tokenStatus === TOKEN_STATUS.EXPIRED) {
      trackEvent({ name: TRACK_EVENTS.ROUTE_SESSION_EXPIRED });
      dispatch(LOGGED_OUT());
    }
  }, [credentials, dispatch, trackEvent]);

  // If not logged, redirect to authentication page.
  if (logged !== "ok") {
    return (
      <Navigate
        to={`/auth?${REDIRECT_PARAM}=${encodeURIComponent(
          `${location.pathname}${location.search}`,
        )}`}
      />
    );
  }

  // Otherwise, render children or the default outlet.
  return <>{children ?? <Outlet />}</>;
}

function RequireRoles({
  children,
  requiredRoles,
}: {
  children?: ReactNode;
  requiredRoles?: AccountRole[];
}) {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { account, activeRoles, careseeker } = useConnecterWithCareseeker();
  const toast = useToast();
  const translations = useTranslations();

  const target = useMemo(
    () =>
      getPriorityRoute(
        account,
        activeRoles,
        careseeker?.product_features ?? undefined,
      ),
    [account, activeRoles, careseeker],
  );

  const requiredRolesSet = useMemo(
    () => Array.from(new Set([...(requiredRoles ?? [])])),
    [requiredRoles],
  );

  const isAllowed = useMemo(
    () =>
      isStaffAccount(account) ||
      requiredRolesSet.some((role) => activeRoles?.includes(role)),
    [account, requiredRolesSet, activeRoles],
  );

  useEffect(() => {
    if (!isAllowed && location.pathname === target) {
      dispatch(LOGGED_OUT());
    }
  }, [isAllowed, location.pathname, target, dispatch]);

  useEffect(() => {
    if (!isAllowed) {
      toast({
        color: "warning",
        message: translations.general.authorizationError,
      });
    }
  }, [isAllowed, translations]);

  if (!isAllowed) {
    return <Navigate replace state={{ from: location }} to={target} />;
  }

  return <>{children ?? <Outlet />}</>;
}

export function CareseekerRedirector() {
  const { careseekerId } = useParams();
  const loggedCareseekerId = useLoggedCareseeker()?.id;
  const careseekers = useAppSelector(selectCareseekers);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const selectedCareseeker = careseekers?.find(
      ({ id }) => id === Number(careseekerId),
    );
    if (
      selectedCareseeker?.id &&
      selectedCareseeker.id !== loggedCareseekerId
    ) {
      dispatch(
        CARESEEKER_CHANGED({
          careseeker_id: selectedCareseeker.id,
          careseeker_name: selectedCareseeker?.name,
          country: selectedCareseeker?.address?.country,
        }),
      );
    }

    // Replace the first segment (careseekerId) with "dashboard"
    const updatedPath = location.pathname.replace(
      `/${careseekerId}`,
      "/dashboard",
    );

    navigate(updatedPath + location.search + location.hash, {
      replace: true,
    });
  }, [careseekerId, loggedCareseekerId, location]);

  return (
    <SpinnerPageContainer>
      <SpinnerPage id="careseeker-redirector" />
    </SpinnerPageContainer>
  );
}

function RoutePatientAuction() {
  const { auction, patient } = usePatientHomeContext();
  const activateRehabFormsFeature = areNewRehabFormsActive(auction);
  const { isRedesignActive: isDocsRedesignActive } =
    useIsDocsAndExtractActive();

  return (
    <Routes>
      <Route element={<SearchCandidatesProvider />}>
        {/* Assessment */}
        <Route
          element={
            <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
              <AssessmentMenu auction={auction} patient={patient} />
            </RequireRoles>
          }
          path="assessing/*"
        >
          <Route
            element={
              <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                <PatientAssessment auction={auction} />
              </RequireRoles>
            }
            index
          />
          <Route
            element={
              <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                <PatientAssessment auction={auction} />
              </RequireRoles>
            }
            path=":assessmentSlug"
          />
        </Route>
        {/* Barthel */}
        <Route
          element={
            <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
              <BarthelSummaryPage auctionId={auction.id} patient={patient} />
            </RequireRoles>
          }
          path="barthel-index"
        />
        <Route
          element={
            <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
              <SingerSummaryPage auctionId={auction.id} patient={patient} />
            </RequireRoles>
          }
          path="singer-index"
        />
        <Route
          element={
            <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
              <EarlyRehabSummaryPage auctionId={auction.id} patient={patient} />
            </RequireRoles>
          }
          path="early-rehab-index"
        />
        {/* Request */}
        <Route
          element={
            <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
              <AuctionResponse />
            </RequireRoles>
          }
          path="request/:requestId"
        />
        {/* Patient Home */}
        <Route
          element={
            <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
              <ExtractDocsProvider auction={auction} patient={patient}>
                <RecareDocsProvider auction={auction} patient={patient}>
                  <PatientHomeMenu auction={auction} />
                </RecareDocsProvider>
              </ExtractDocsProvider>
            </RequireRoles>
          }
          path="*"
        >
          <Route
            element={
              <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                <PatientSearch auction={auction} patient={patient} />
              </RequireRoles>
            }
            path={PATIENT_TAB_SEARCH}
          />
          <Route
            element={
              <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                <PatientProfile auction={auction} />
              </RequireRoles>
            }
            path={PATIENT_TAB_PROFILE}
          />
          <Route
            element={
              <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                <RequireCondition
                  hasCondition={isFilesActivatedAtAuctionLevel(auction)}
                  redirectTo={PATIENT_TAB_SEARCH}
                />
              </RequireRoles>
            }
          >
            <Route
              element={
                <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                  <PatientFiles auction={auction} patient={patient} />
                </RequireRoles>
              }
              path={PATIENT_TAB_FILES}
            />
            <Route
              element={
                <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                  <PatientRehabFormsHome auction={auction} patient={patient} />
                </RequireRoles>
              }
              path={`${PATIENT_TAB_FILES}/${PATIENT_TAB_REHAB_FORMS}`}
            />
          </Route>
          <Route
            element={
              <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                <PatientTimeline auction={auction} patient={patient} />
              </RequireRoles>
            }
            path={PATIENT_TAB_TIMELINE}
          />
          <Route
            element={
              <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                <RequireCondition
                  hasCondition={
                    !!getTransitionalCareAction(auction) &&
                    !isDocsRedesignActive
                  }
                  redirectTo={PATIENT_TAB_SEARCH}
                />
              </RequireRoles>
            }
          >
            <Route
              element={
                <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                  <PatientTransitionalCare
                    auction={auction}
                    patient={patient}
                  />
                </RequireRoles>
              }
              path={PATIENT_TAB_TRANSITIONAL_CARE}
            />
          </Route>
          <Route
            element={
              <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                <PatientMap auction={auction} patient={patient} />
              </RequireRoles>
            }
            path={PATIENT_TAB_MAP}
          />
          <Route
            element={
              <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                <RequireCondition
                  hasCondition={
                    activateRehabFormsFeature && !isDocsRedesignActive
                  }
                  redirectTo={PATIENT_TAB_SEARCH}
                />
              </RequireRoles>
            }
          >
            <Route
              element={
                <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                  <PatientRehabFormsHome auction={auction} patient={patient} />
                </RequireRoles>
              }
              path={PATIENT_TAB_REHAB_FORMS}
            />
            <Route
              element={
                <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                  <RehabFormRenderer auction={auction} patient={patient} />
                </RequireRoles>
              }
              path={`${PATIENT_TAB_REHAB_FORMS}/:form`}
            />
          </Route>
          <Route
            element={<Navigate replace to={PATIENT_TAB_SEARCH} />}
            path="*"
          />
        </Route>
      </Route>
      <Route element={<DischargeConfirmation />} path="discharge" />
      <Route
        element={<PrintableSearchMergePanel />}
        path="printable-search-panel"
      />
    </Routes>
  );
}

export default function CareseekerRoutes() {
  const [defaultRoute, setDefaultRoute] = useState(defaultRedirectPath);

  return (
    <Routes>
      <Route element={<RedirectRoot to={defaultRoute} />} index />
      <Route element={<RootPage appName="clinic" />} path="/">
        <Route element={<CareseekerAuthPage />} path="auth" />
        <Route element={<CareseekerAuthPage />} path="auth/:token" />
        <Route element={<DiagnosticPage />} path="diagnostic" />
        <Route element={<ForgotPasswordPage />} path="forgot-password" />
        <Route element={<EmptyPage />} path="home" />
        <Route element={<DebugPage />} path="info" />
        <Route element={<LogoutPage />} path="logout" />
        <Route element={<ThrowError />} path="throwerror" />
        <Route element={<UnsupportedBrowser />} path="unsupported_browser" />
        <Route path="app/*">
          <Route element={<CareseekerAuthCheck />} path="clinic/*">
            <Route path=":careseekerId/*">
              <Route
                element={<PrintableSenderDashboardPage />}
                path="dashboard-print"
              />
              <Route element={<CareseekerRedirector />} path="*" />
            </Route>
            <Route path="dashboard/*">
              <Route element={<ClinicDashboard />}>
                <Route
                  element={
                    <CheckRoles
                      defaultRoute={defaultRoute}
                      setDefaultRoute={setDefaultRoute}
                    />
                  }
                >
                  <Route path="todo">
                    <Route
                      element={
                        <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                          <SenderDashboardPage />
                        </RequireRoles>
                      }
                      path=":tab"
                    />
                    <Route
                      element={<Navigate replace to={defaultRoute} />}
                      path="*"
                    />
                  </Route>
                  <Route
                    element={<Navigate replace to={onpremDebugRedirectPath} />}
                    path="debug-on-premise"
                  />
                  <Route
                    element={
                      <RequireRoles requiredRoles={[ACCOUNT_ROLE_SENDER_IT]}>
                        <OnPremiseDebugPage />
                      </RequireRoles>
                    }
                    path="onpremsetup/:page"
                  />
                  <Route
                    element={
                      <RequireRoles requiredRoles={[ACCOUNT_ROLE_DTA_APPROVER]}>
                        <RehabFormApprovalPage />
                      </RequireRoles>
                    }
                    path={REHAB_FORM_APPROVAL_PAGE}
                  />
                  <Route
                    element={
                      <RequireRoles
                        requiredRoles={[
                          ACCOUNT_ROLE_CARESEEKER_USER_MANAGEMENT,
                        ]}
                      >
                        <UserManagementPage />
                      </RequireRoles>
                    }
                    path="users"
                  />
                  <Route
                    element={
                      <RequireRoles requiredRoles={[ACCOUNT_ROLE_REPORT]}>
                        <ReportPage />
                      </RequireRoles>
                    }
                    path="report"
                  />
                  <Route
                    element={
                      <RequireRoles requiredRoles={[ACCOUNT_ROLE_PREDICT]}>
                        <RequireCondition
                          hasCondition={activatePredict}
                          redirectTo={defaultRoute}
                        />
                      </RequireRoles>
                    }
                    path="predict"
                  >
                    <Route element={<PredictPage />} index />
                    <Route element={<PredictStationGuard />} path=":patientId">
                      <Route element={<PatientDetailsPage />} index />
                    </Route>
                  </Route>
                  <Route
                    element={<RequireAiChatAccess redirectTo={defaultRoute} />}
                    path="chat"
                  >
                    <Route element={<AiChat />} index />
                    <Route element={<AiChat />} path=":chatId" />
                  </Route>
                  <Route
                    element={
                      <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                        <ReportPage isTestQuickSightDashboard />
                      </RequireRoles>
                    }
                    path="report_test"
                  />
                  <Route
                    element={
                      <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                        <PatientCreatePage />
                      </RequireRoles>
                    }
                    path="patient/new"
                  />
                  <Route
                    element={
                      <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                        <PatientHome />
                      </RequireRoles>
                    }
                    path="patient/:patientId"
                  >
                    <Route
                      element={
                        <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                          <RoutePatientAuction />
                        </RequireRoles>
                      }
                      path={"auction/:auctionId/*"}
                    />
                  </Route>
                  <Route
                    element={
                      <RequireRoles requiredRoles={CREATE_PATIENT_ROLES}>
                        <PasswordChangePageV2 />
                      </RequireRoles>
                    }
                    path="my-password-change"
                  />
                  <Route
                    element={<Navigate replace to={defaultRoute} />}
                    path="*"
                  />
                </Route>
              </Route>
              <Route
                element={<Navigate replace to={defaultRoute} />}
                path="*"
              />
            </Route>

            <Route element={<Navigate replace to={defaultRoute} />} path="*" />
          </Route>
          <Route element={<Navigate replace to={defaultRoute} />} path="*" />
        </Route>
        <Route
          element={<PasswordResetPageV2 authRoute="/app/auth" />}
          path="reset/:token"
        />
        <Route element={<RedirectRoot to={defaultRoute} />} path="*" />
      </Route>
    </Routes>
  );
}
